/**
 * Group Service - Business Logic for Group Management
 * 
 * Pure functions for group operations.
 * Following dev guide: no DOM imports, deterministic operations.
 */

import { 
  Group, 
  Participant, 
  CreateGroupParams, 
  AddParticipantParams,
  GroupCreationResult,
  SplitMode,
  GroupStatus,
  SCHEMA_VERSION 
} from './groupModel';
import { generateGroupId, generateParticipantId } from '../../utils/id';
import { groupStorage } from './storage';
import { 
  validateCreateGroupParams, 
  validateAddParticipantParams,
  validateParticipantName,
  validateGroupName,
  sanitizeGroupName,
  sanitizeParticipantName
} from './validation';
import { generateShareUrl } from './shareLink';

/**
 * Create a new group with initial creator as first participant
 */
export function createGroup(params: CreateGroupParams): GroupCreationResult {
  // Validate and sanitize input
  const validatedParams = validateCreateGroupParams(params);
  const sanitizedName = sanitizeGroupName(validatedParams.name);
  const sanitizedCreatorName = sanitizeParticipantName(validatedParams.creatorName);
  
  // Additional business validation
  validateGroupName(sanitizedName);
  
  const groupId = generateGroupId();
  const now = new Date().toISOString();
  
  // Create the creator as the first participant
  const creator: Participant = {
    id: generateParticipantId(),
    name: sanitizedCreatorName,
    joinedAt: now
  };

  const group: Group = {
    id: groupId,
    name: sanitizedName,
    description: validatedParams.description,
    createdAt: now,
    updatedAt: now,
    schemaVersion: SCHEMA_VERSION,
    
    participants: [creator],
    expenses: [],
    
    defaultCurrency: validatedParams.defaultCurrency || 'USD',
    defaultSplitMode: SplitMode.EQUAL,
    
    status: GroupStatus.ACTIVE
  };

  // Save to localStorage
  groupStorage.saveGroup(group);
  groupStorage.setCurrentGroup(groupId);

  // Generate shareable URL with embedded group data
  const shareUrl = generateShareUrl(group);

  return {
    group,
    shareUrl
  };
}

/**
 * Add a participant to an existing group
 */
export function addParticipant(groupId: string, params: AddParticipantParams): Group | null {
  const group = groupStorage.loadGroup(groupId);
  if (!group) {
    return null;
  }

  // Validate and sanitize input
  const validatedParams = validateAddParticipantParams(params);
  const sanitizedName = sanitizeParticipantName(validatedParams.name);
  
  // Business validation
  validateParticipantName(sanitizedName, group.participants);

  const newParticipant: Participant = {
    id: generateParticipantId(),
    name: sanitizedName,
    email: validatedParams.email,
    joinedAt: new Date().toISOString()
  };

  const updatedGroup: Group = {
    ...group,
    participants: [...group.participants, newParticipant],
    updatedAt: new Date().toISOString()
  };

  groupStorage.saveGroup(updatedGroup);
  return updatedGroup;
}

/**
 * Remove a participant from a group
 */
export function removeParticipant(groupId: string, participantId: string): Group | null {
  const group = groupStorage.loadGroup(groupId);
  if (!group) {
    return null;
  }

  // Don't allow removing if they're involved in expenses
  const hasExpenses = group.expenses.some(expense => 
    expense.paidBy === participantId || 
    expense.participantShares.some(share => share.participantId === participantId && share.isIncluded)
  );

  if (hasExpenses) {
    throw new Error('Cannot remove participant who is involved in expenses');
  }

  // Don't allow removing the last participant
  if (group.participants.length <= 1) {
    throw new Error('Cannot remove the last participant');
  }

  const updatedGroup: Group = {
    ...group,
    participants: group.participants.filter(p => p.id !== participantId),
    updatedAt: new Date().toISOString()
  };

  groupStorage.saveGroup(updatedGroup);
  return updatedGroup;
}

/**
 * Update group basic information
 */
export function updateGroup(groupId: string, updates: Partial<Pick<Group, 'name' | 'description' | 'defaultCurrency'>>): Group | null {
  const group = groupStorage.loadGroup(groupId);
  if (!group) {
    return null;
  }

  const updatedGroup: Group = {
    ...group,
    ...updates,
    updatedAt: new Date().toISOString()
  };

  groupStorage.saveGroup(updatedGroup);
  return updatedGroup;
}

/**
 * Load a group by ID with error handling
 */
export function loadGroup(groupId: string): Group | null {
  return groupStorage.loadGroup(groupId);
}

/**
 * Get recent groups for the landing page
 */
export function getRecentGroups(): Group[] {
  return groupStorage.getRecentGroups();
}
